/**
 * @quantabit/sdk-config - DataSanitizer
 * 
 * 数据脱敏工具
 * 自动检测和脱敏 PII (个人身份信息)
 * 用于日志、错误报告、分析数据中的隐私保护
 */

// ============ PII 检测模式 ============

const PII_PATTERNS = {
  // 邮箱地址
  email: {
    regex: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    mask: (match) => {
      const [local, domain] = match.split('@');
      return `${local[0]}***@${domain}`;
    },
  },
  // 手机号码 (国际格式)
  phone: {
    regex: /(\+?\d{1,4}[-.\s]?)?\(?\d{2,4}\)?[-.\s]?\d{3,4}[-.\s]?\d{3,4}/g,
    mask: (match) => {
      const digits = match.replace(/\D/g, '');
      if (digits.length < 7) return match; // 太短不是手机号
      return `${digits.slice(0, 3)}****${digits.slice(-4)}`;
    },
  },
  // 身份证号 (中国)
  idCard: {
    regex: /\d{6}(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[0-9Xx]/g,
    mask: (match) => `${match.slice(0, 6)}********${match.slice(-4)}`,
  },
  // 钱包地址 (Ethereum-style)
  walletAddress: {
    regex: /0x[a-fA-F0-9]{40}/g,
    mask: (match) => `${match.slice(0, 6)}...${match.slice(-4)}`,
  },
  // IP 地址
  ipAddress: {
    regex: /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
    mask: (match) => {
      const parts = match.split('.');
      return `${parts[0]}.${parts[1]}.*.*`;
    },
  },
  // JWT Token
  jwtToken: {
    regex: /eyJ[a-zA-Z0-9_-]+\.eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/g,
    mask: () => '[REDACTED_TOKEN]',
  },
  // API Key patterns
  apiKey: {
    regex: /(api[_-]?key|apikey|secret|password|token|auth)[=:]\s*['"]?([a-zA-Z0-9_\-./+]{20,})['"]?/gi,
    mask: (match) => {
      const colonIdx = match.indexOf(':');
      const equalIdx = match.indexOf('=');
      const sepIdx = colonIdx >= 0 ? colonIdx : equalIdx;
      const key = match.slice(0, sepIdx + 1);
      return `${key} [REDACTED]`;
    },
  },
  // 信用卡号 (Visa/MC/Amex 等常见格式)
  creditCard: {
    regex: /\b(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|6(?:011|5[0-9]{2})[0-9]{12})\b/g,
    mask: (match) => `****-****-****-${match.slice(-4)}`,
  },
  // DID (去中心化身份标识符)
  did: {
    regex: /did:[a-z]+:[a-zA-Z0-9._%-]+/g,
    mask: (match) => {
      const parts = match.split(':');
      if (parts.length >= 3) {
        const id = parts[2];
        return `${parts[0]}:${parts[1]}:${id.slice(0, 6)}...${id.slice(-4)}`;
      }
      return `${match.slice(0, 12)}...`;
    },
  },
  // SSN (美国社会安全号)
  ssn: {
    regex: /\b\d{3}-\d{2}-\d{4}\b/g,
    mask: () => '***-**-****',
  },
};

// ============ 敏感字段名 ============

const SENSITIVE_FIELDS = new Set([
  'password', 'passwd', 'secret', 'token', 'accessToken', 'refreshToken',
  'access_token', 'refresh_token', 'api_key', 'apiKey', 'privateKey',
  'private_key', 'ssn', 'socialSecurityNumber', 'creditCard', 'credit_card',
  'cardNumber', 'card_number', 'cvv', 'pin', 'otp', 'mnemonic', 'seed',
  'seedPhrase', 'seed_phrase', 'idNumber', 'id_number', 'passport',
  // Web3 / DeFi 扩展
  'keystore', 'encryptedKey', 'encrypted_key', 'recoveryPhrase', 'recovery_phrase',
  'walletSeed', 'wallet_seed', 'derivationPath', 'derivation_path',
  'secretKey', 'secret_key', 'signingKey', 'signing_key',
  // 支付安全
  'cardCvv', 'card_cvv', 'securityCode', 'security_code',
  'expiryDate', 'expiry_date', 'paymentPassword', 'payment_password',
]);

// ============ DataSanitizer ============

class DataSanitizerClass {
  constructor() {
    this._customRules = [];
    this._whitelistFields = new Set();
  }

  /**
   * 脱敏文本内容 — 自动检测并掩码 PII
   * @param {string} text - 原始文本
   * @param {string[]} types - 要检测的 PII 类型 (默认全部)
   * @returns {string} 脱敏后的文本
   */
  sanitizeText(text, types = null) {
    if (typeof text !== 'string') return text;

    let result = text;
    const patterns = types 
      ? Object.entries(PII_PATTERNS).filter(([key]) => types.includes(key))
      : Object.entries(PII_PATTERNS);

    for (const [, { regex, mask }] of patterns) {
      // 因为 regex 有 /g 标志，需要重置 lastIndex
      regex.lastIndex = 0;
      result = result.replace(regex, mask);
    }

    // 应用自定义规则
    for (const rule of this._customRules) {
      rule.regex.lastIndex = 0;
      result = result.replace(rule.regex, rule.mask);
    }

    return result;
  }

  /**
   * 脱敏对象 — 递归处理所有字段
   * @param {object} obj - 原始对象
   * @param {object} options - 选项
   * @returns {object} 脱敏后的深拷贝对象
   */
  sanitizeObject(obj, options = {}) {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item, options));
    }

    const maxDepth = options.maxDepth || 10;
    const currentDepth = options._currentDepth || 0;

    if (currentDepth >= maxDepth) {
      return '[MAX_DEPTH_REACHED]';
    }

    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      // 白名单字段跳过
      if (this._whitelistFields.has(key)) {
        result[key] = value;
        continue;
      }

      // 检查是否为敏感字段
      if (SENSITIVE_FIELDS.has(key) || SENSITIVE_FIELDS.has(key.toLowerCase())) {
        result[key] = typeof value === 'string' && value.length > 0 
          ? '[REDACTED]' 
          : value;
        continue;
      }

      // 递归处理对象
      if (typeof value === 'object' && value !== null) {
        result[key] = this.sanitizeObject(value, {
          ...options,
          _currentDepth: currentDepth + 1,
        });
      } else if (typeof value === 'string') {
        result[key] = this.sanitizeText(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  /**
   * 脱敏 URL — 移除查询参数中的敏感信息
   * @param {string} url - 原始 URL
   * @returns {string} 脱敏后的 URL
   */
  sanitizeUrl(url) {
    if (typeof url !== 'string') return url;

    try {
      const parsed = new URL(url, 'https://placeholder.com');
      const sensitiveParams = ['token', 'key', 'secret', 'password', 'auth', 'api_key', 'access_token'];
      
      for (const param of sensitiveParams) {
        if (parsed.searchParams.has(param)) {
          parsed.searchParams.set(param, '[REDACTED]');
        }
      }

      // 如果原始 URL 是相对路径
      if (!url.startsWith('http')) {
        return parsed.pathname + parsed.search + parsed.hash;
      }
      return parsed.toString();
    } catch {
      // URL 解析失败，使用文本脱敏
      return this.sanitizeText(url);
    }
  }

  /**
   * 创建安全的日志对象
   * 用于错误报告和审计日志
   * 
   * @param {object} data - 原始数据
   * @returns {object} 安全的日志数据
   */
  createSafeLogEntry(data) {
    return this.sanitizeObject(data);
  }

  /**
   * 添加自定义脱敏规则
   * @param {string} name - 规则名称
   * @param {RegExp} regex - 匹配正则 (必须有 /g 标志)
   * @param {function} mask - 掩码函数 (match) => maskedString
   */
  addRule(name, regex, mask) {
    this._customRules.push({ name, regex, mask });
  }

  /**
   * 添加白名单字段 — 这些字段不会被脱敏
   * @param {string[]} fields - 字段名列表
   */
  addWhitelist(fields) {
    fields.forEach(f => this._whitelistFields.add(f));
  }

  /**
   * 匿名化用户数据
   * 用于分析统计时的数据处理
   * 
   * @param {object} userData - 用户数据
   * @returns {object} 匿名化后的数据
   */
  anonymize(userData) {
    if (!userData || typeof userData !== 'object') return userData;

    const anonymized = { ...userData };

    // 替换直接标识符
    if (anonymized.email) anonymized.email = this._hashString(anonymized.email);
    if (anonymized.phone) anonymized.phone = this._hashString(anonymized.phone);
    if (anonymized.name) delete anonymized.name;
    if (anonymized.realName) delete anonymized.realName;
    if (anonymized.real_name) delete anonymized.real_name;
    if (anonymized.address) delete anonymized.address;
    if (anonymized.ip) anonymized.ip = this._anonymizeIp(anonymized.ip);
    if (anonymized.walletAddress) {
      anonymized.walletAddress = anonymized.walletAddress.slice(0, 6) + '...';
    }

    // 保留非标识性数据用于分析
    // 如：国家、语言、设备类型、浏览器类型等

    return anonymized;
  }

  // ============ 内部方法 ============

  /**
   * 简单哈希 — 用于匿名化（非加密安全）
   */
  _hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return `anon_${Math.abs(hash).toString(36)}`;
  }

  _anonymizeIp(ip) {
    if (typeof ip !== 'string') return ip;
    const parts = ip.split('.');
    if (parts.length === 4) {
      return `${parts[0]}.${parts[1]}.0.0`;
    }
    return ip;
  }

  /**
   * 一键日志脱敏 — 所有 SDK 通用的日志安全输出
   * 自动检测并脱敏 PII、Token、API Key 等敏感数据
   * 
   * @param {...*} args - 任意数量的日志参数
   * @returns {string[]} 脱敏后的日志参数数组
   * 
   * @example
   * // 替代 console.log(user.email, token)
   * console.log(...dataSanitizer.sanitizeForLogging(user.email, token));
   */
  sanitizeForLogging(...args) {
    return args.map(arg => {
      if (typeof arg === 'string') {
        return this.sanitizeText(arg);
      }
      if (arg && typeof arg === 'object') {
        try {
          return JSON.parse(JSON.stringify(arg, (key, value) => {
            if (typeof value === 'string') {
              return this.sanitizeText(value);
            }
            // 脱敏常见敏感字段名
            const sensitiveKeys = ['password', 'secret', 'token', 'apiKey', 'api_key', 
                                   'authorization', 'cookie', 'session', 'private_key',
                                   'mnemonic', 'seed', 'passphrase'];
            if (sensitiveKeys.includes(key.toLowerCase())) {
              return '[REDACTED]';
            }
            return value;
          }));
        } catch {
          return '[Object - sanitize failed]';
        }
      }
      return arg;
    });
  }

  /**
   * 完全移除所有 PII — GDPR 数据删除场景
   * 比 sanitizeText 更激进，直接替换为占位符
   * 
   * @param {*} data - 任意数据
   * @returns {*} PII 完全移除后的数据
   */
  redactAllPII(data) {
    if (typeof data === 'string') {
      let result = data;
      for (const { regex } of Object.values(PII_PATTERNS)) {
        const freshRegex = new RegExp(regex.source, regex.flags);
        result = result.replace(freshRegex, '[PII_REMOVED]');
      }
      return result;
    }
    if (Array.isArray(data)) {
      return data.map(item => this.redactAllPII(item));
    }
    if (data && typeof data === 'object') {
      const result = {};
      for (const [key, value] of Object.entries(data)) {
        result[key] = this.redactAllPII(value);
      }
      return result;
    }
    return data;
  }
}

// ============ 单例导出 ============

export const dataSanitizer = new DataSanitizerClass();
export default dataSanitizer;
